import React, { useState, useEffect } from 'react';
import { User, Mail, Key, Edit, Save, X, Eye, EyeOff, Shield, Calendar } from 'lucide-react';
import { useAuth } from '../../providers/AuthProvider';
import { ApiService } from '../../services/api';
import { copyToClipboard } from '../../utils/copyToClipboard';
import toast from 'react-hot-toast';

interface AdminProfile {
  id: string;
  email: string;
  role: string;
  walletAddress?: string;
  createdAt: string;
}

const AdminInfo: React.FC = () => {
  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [editForm, setEditForm] = useState({
    email: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [saving, setSaving] = useState(false);
  const { } = useAuth();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const profileData = await ApiService.getProfile();
      setProfile({
        ...profileData.user,
        createdAt: new Date().toISOString(), // Default value since it's not in the API response
      });
      setEditForm({
        email: profileData.user.email,
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile information');
    } finally {
      setLoading(false);
    }
  };

  const handleEditProfile = () => {
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setEditForm({
      email: profile?.email || '',
    });
  };

  const handleSaveProfile = async () => {
    if (!profile) return;

    setSaving(true);
    try {
      const response = await ApiService.updateProfile(editForm);
      
      setProfile({
        ...profile,
        email: response.user.email,
      });
      setEditing(false);
      toast.success(response.message);
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    setSaving(true);
    try {
      await ApiService.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setShowPasswordForm(false);
      toast.success('Password changed successfully');
    } catch (error: any) {
      console.error('Failed to change password:', error);
      toast.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Failed to load profile information</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Admin Information</h1>
          <p className="text-sm sm:text-base text-gray-400">Manage your admin account settings and profile</p>
        </div>
        {!editing && (
          <button
            onClick={handleEditProfile}
            className="inline-flex items-center px-4 py-2 border border-[#567095]/30 rounded-md shadow-sm text-sm font-medium text-white bg-[#182233] hover:bg-[#567095]/20"
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Profile
          </button>
        )}
      </div>

      {/* Profile Information */}
      <div className="bg-[#182233] shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center mb-4">
            <User className="h-6 w-6 text-gray-400 mr-2" />
            <h3 className="text-lg leading-6 font-medium text-white">Profile Information</h3>
          </div>
          
          <div className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Email Address</label>
              {editing ? (
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full appearance-none rounded-md border-0 bg-[#567095]/20 px-3 py-2 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
                  placeholder="Enter email address"
                  required
                />
              ) : (
                <div className="flex items-center justify-between p-3 bg-[#567095]/10 rounded-md">
                  <div className="flex items-center">
                    <Mail className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-white">{profile.email}</span>
                  </div>
                  <button
                    onClick={() => copyToClipboard(profile.email)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Copy
                  </button>
                </div>
              )}
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Role</label>
              <div className="flex items-center p-3 bg-[#567095]/10 rounded-md">
                <Shield className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm font-semibold text-white uppercase">{profile.role}</span>
              </div>
            </div>

            {/* Wallet Address */}
            {profile.walletAddress && (
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Connected Wallet</label>
                <div className="flex items-center justify-between p-3 bg-[#567095]/10 rounded-md">
                  <div className="flex items-center">
                    <Key className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm font-mono text-white">
                      {profile.walletAddress}
                    </span>
                  </div>
                  <button
                    onClick={() => copyToClipboard(profile.walletAddress || '')}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}

            {/* Account Created */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Account Created</label>
              <div className="flex items-center p-3 bg-[#567095]/10 rounded-md">
                <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm text-white">
                  {new Date(profile.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            </div>
          </div>

          {/* Edit Actions */}
          {editing && (
            <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3 sm:space-x-0 mt-6">
              <button
                onClick={handleCancelEdit}
                className="w-full sm:w-auto px-4 py-2 border border-[#567095]/30 text-sm font-medium rounded-md text-white bg-[#182233] hover:bg-[#567095]/20 flex items-center justify-center sm:justify-start"
                disabled={saving}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </button>
              <button
                onClick={handleSaveProfile}
                className="w-full sm:w-auto px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center sm:justify-start"
                disabled={saving}
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Security Settings */}
      <div className="bg-[#182233] shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Key className="h-6 w-6 text-gray-400 mr-2" />
              <h3 className="text-lg leading-6 font-medium text-white">Security Settings</h3>
            </div>
            {!showPasswordForm && (
              <button
                onClick={() => setShowPasswordForm(true)}
                className="inline-flex items-center px-3 py-2 border border-[#567095]/30 rounded-md shadow-sm text-sm font-medium text-white bg-[#182233] hover:bg-[#567095]/20"
              >
                Change Password
              </button>
            )}
          </div>

          {showPasswordForm ? (
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    className="w-full appearance-none rounded-md border-0 bg-[#567095]/20 px-3 py-2 pr-10 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
                    placeholder="Enter current password"
                    required
                    disabled={saving}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className="w-full appearance-none rounded-md border-0 bg-[#567095]/20 px-3 py-2 pr-10 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
                    placeholder="Enter new password"
                    required
                    disabled={saving}
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Password must be at least 8 characters long</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    className="w-full appearance-none rounded-md border-0 bg-[#567095]/20 px-3 py-2 pr-10 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
                    placeholder="Confirm new password"
                    required
                    disabled={saving}
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3 sm:space-x-0">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordForm({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: '',
                    });
                  }}
                  className="w-full sm:w-auto px-4 py-2 border border-[#567095]/30 text-sm font-medium rounded-md text-white bg-[#182233] hover:bg-[#567095]/20"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  disabled={saving}
                >
                  {saving ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          ) : (
            <div className="text-sm text-gray-500">
              <p>Keep your account secure by using a strong password and updating it regularly.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminInfo;